import React from "react";
import {
  ShieldCheck,
  CheckCircle2,
  FileCheck2,
  FileClock,
  FileText,
  BadgePercent,
  Wallet,
  Landmark,
  Euro,
  Clock3,
  Save,
  CircleHelp,
  Home,
  PiggyBank,
  Building2,
  ChevronRight,
  ArrowLeft,
  MapPin,
  AlertTriangle,
  Plus,
  Trash2,
  Download,
  X,
  LoaderCircle,
  Info,
  LockKeyhole,
  CalendarDays,
} from "lucide-react";
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import "./LenderSubmitDecisionPage.css";

import { useToast } from "../../../../components/feedback/ToastProvider";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "";

/* =========================================================
   EMPTY FORMS
   ========================================================= */

const EMPTY_GRANT_FORM = {
  approved_loan_amount: "",
  purchase_loan_amount: "",
  renovation_loan_amount: "",
  own_contribution: "",
  tenure_years: "",
  indicative_apr: "",
  estimated_monthly_payment: "",
  mortgage_amount: "",
  mortgage_mandate_amount: "",
  conditions: "",
};

const EMPTY_CLOSE_FORM = {
  closure_reason: "",
};

/* =========================================================
   NUMBER HELPERS
   ========================================================= */

function sanitizeFormattedNumber(value = "", { allowDecimal = false } = {}) {
  const raw = String(value).replace(/[^\d.,]/g, "");

  if (!raw) {
    return "";
  }

  if (!allowDecimal) {
    return raw.replace(/[^\d]/g, "");
  }

  const lastComma = raw.lastIndexOf(",");

  const lastDot = raw.lastIndexOf(".");

  const decimalIndex = Math.max(lastComma, lastDot);

  if (decimalIndex === -1) {
    return raw.replace(/[^\d]/g, "");
  }

  const integerPart = raw.slice(0, decimalIndex).replace(/[^\d]/g, "");

  const decimalPart = raw
    .slice(decimalIndex + 1)
    .replace(/[^\d]/g, "")
    .slice(0, 2);

  if ((!decimalPart && raw.endsWith(".")) || raw.endsWith(",")) {
    return `${integerPart}.`;
  }

  return `${integerPart}.${decimalPart}`;
}

function formatNumberDisplay(value = "", { allowDecimal = false } = {}) {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  const text = String(value).trim();

  if (!allowDecimal) {
    const normalized = text.replace(/,/g, "");

    const numericValue = Number(normalized);

    if (!Number.isFinite(numericValue)) {
      return "";
    }

    return Math.trunc(numericValue).toLocaleString("en-US");
  }

  const normalized = text.replace(",", ".");

  const numericValue = Number(normalized);

  if (!Number.isFinite(numericValue)) {
    return "";
  }

  if (text.endsWith(".") || text.endsWith(",")) {
    return `${Math.trunc(numericValue).toLocaleString("en-US")}.`;
  }

  const [integerPart, decimalPart] = normalized.split(".");

  const formattedInteger = Number(integerPart || 0).toLocaleString("en-US");

  return decimalPart !== undefined
    ? `${formattedInteger}.` + `${decimalPart}`
    : formattedInteger;
}

function normalizeApiNumericValue(value, { allowDecimal = false } = {}) {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  const normalized = String(value).trim().replace(",", ".");

  const numericValue = Number(normalized);

  if (!Number.isFinite(numericValue)) {
    return "";
  }

  if (!allowDecimal) {
    return String(Math.trunc(numericValue));
  }

  return String(numericValue);
}

function toNumericValue(value) {
  if (value === null || value === undefined || value === "") {
    return 0;
  }

  const normalized = String(value).replace(/,/g, "").trim();

  const number = Number(normalized);

  return Number.isFinite(number) ? number : 0;
}

function hasNumericInput(value) {
  return value !== null && value !== undefined && String(value).trim() !== "";
}

function valuesAreEqual(first, second, tolerance = 0.01) {
  return Math.abs(first - second) <= tolerance;
}

/* =========================================================
   DISPLAY HELPERS
   ========================================================= */

function formatCurrencyDisplay(value) {
  if (value === null || value === undefined || value === "") {
    return "—";
  }

  const normalizedValue =
    typeof value === "string" ? value.replace(",", ".").trim() : value;

  const numericValue = Number(normalizedValue);

  if (!Number.isFinite(numericValue)) {
    return "—";
  }

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(numericValue);
}

function formatFileSize(bytes) {
  const numericValue = Number(bytes);

  if (!Number.isFinite(numericValue) || numericValue <= 0) {
    return "";
  }

  if (numericValue < 1024) {
    return `${numericValue} B`;
  }

  if (numericValue < 1024 * 1024) {
    return `${(numericValue / 1024).toFixed(1)} KB`;
  }

  return `${(numericValue / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDocumentDate(value) {
  if (!value) {
    return "";
  }

  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) {
    return "";
  }

  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(parsed);
}

function formatEnumLabel(value) {
  if (!value) {
    return "—";
  }

  return String(value)
    .toLowerCase()
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function getInitials(name = "") {
  const parts = name.trim().split(/\s+/).filter(Boolean);

  if (!parts.length) {
    return "—";
  }

  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }

  return (`${parts[0][0]}` + `${parts[parts.length - 1][0]}`).toUpperCase();
}

function formatPropertyAddress(property, countryLabel) {
  if (!property) {
    return "—";
  }

  if (property.full_address?.trim()) {
    return property.full_address.trim();
  }

  const street = String(property.street || "").trim();

  const houseNumber = String(property.house_number || "").trim();

  const postalCode = String(property.postal_code || "").trim();

  const city = String(property.city || "").trim();

  const country = String(
    property.country_label || countryLabel || property.country_code || "",
  ).trim();

  const streetLine = [street, houseNumber].filter(Boolean).join(" ");

  const localityLine = [postalCode, city].filter(Boolean).join(" ");

  return [streetLine, localityLine, country].filter(Boolean).join(", ") || "—";
}

/* =========================================================
   CALCULATION
   ========================================================= */

function calculateMonthlyPayment(principal, annualApr, tenureYears) {
  const loanAmount = toNumericValue(principal);

  const apr = toNumericValue(annualApr);

  const years = toNumericValue(tenureYears);

  if (loanAmount <= 0 || apr < 0 || years <= 0) {
    return null;
  }

  const numberOfPayments = years * 12;

  const monthlyRate = apr / 100 / 12;

  if (monthlyRate === 0) {
    return loanAmount / numberOfPayments;
  }

  const compoundFactor = Math.pow(1 + monthlyRate, numberOfPayments);

  return loanAmount * ((monthlyRate * compoundFactor) / (compoundFactor - 1));
}

/* =========================================================
   COMMON COMPONENTS
   ========================================================= */

function Tooltip({ text }) {
  return (
    <span className="decision-tooltip" tabIndex={0}>
      <CircleHelp size={14} />

      <span className="decision-tooltip__bubble">{text}</span>
    </span>
  );
}

function ContextField({ label, value, wide = false, icon = null }) {
  return (
    <div
      className={`decision-context-field ${
        wide ? "decision-context-field--wide" : ""
      }`}
    >
      <span className="decision-context-field__label">
        {icon ? (
          <span className="decision-context-field__label-icon">{icon}</span>
        ) : null}

        {label}
      </span>

      <strong className="decision-context-field__value">{value || "—"}</strong>
    </div>
  );
}

function ApplicantContextField({ applicant }) {
  return (
    <div className="decision-context-field decision-context-field--applicant">
      <div className="decision-context-field__label">Primary homebuyer</div>

      <div className="decision-context-field__applicant">
        <div className="decision-context-field__avatar">
          {applicant?.image_url ? (
            <img
              src={applicant.image_url}
              alt={applicant?.name || ""}
              className="decision-context-field__avatar-image"
            />
          ) : (
            <span className="decision-context-field__avatar-initials">
              {getInitials(applicant?.name)}
            </span>
          )}
        </div>

        <div className="decision-context-field__applicant-details">
          <div className="decision-context-field__value">
            {applicant?.name || "—"}
          </div>

          {applicant?.email ? (
            <div className="decision-context-field__applicant-subtitle">
              {applicant.email}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function NumericField({
  icon,
  label,
  tooltip,
  value,
  onChange,
  placeholder,
  allowDecimal = false,
  readOnly = false,
  error = "",
  warning = "",
}) {
  const inputRef = React.useRef(null);

  const messageId = React.useId();

  const handleChange = (event) => {
    if (readOnly) {
      return;
    }

    const input = event.target;

    const selectionStart = input.selectionStart ?? input.value.length;

    const digitsBeforeCaret = input.value
      .slice(0, selectionStart)
      .replace(/[^\d]/g, "").length;

    const normalized = sanitizeFormattedNumber(input.value, {
      allowDecimal,
    });

    onChange(normalized);

    requestAnimationFrame(() => {
      const element = inputRef.current;

      if (!element) {
        return;
      }

      const formatted = formatNumberDisplay(normalized, {
        allowDecimal,
      });

      let seenDigits = 0;
      let caret = formatted.length;

      for (let index = 0; index < formatted.length; index += 1) {
        if (/\d/.test(formatted[index])) {
          seenDigits += 1;
        }

        if (seenDigits >= digitsBeforeCaret) {
          caret = index + 1;

          break;
        }
      }

      try {
        element.setSelectionRange(caret, caret);
      } catch {}
    });
  };

  const validationMessage = error || warning;

  return (
    <div
      className={`decision-field ${
        error
          ? "decision-field--error"
          : warning
            ? "decision-field--warning"
            : ""
      }`}
    >
      <label className="decision-field__label">
        <span className="decision-field__label-main">
          <span className="decision-field__icon">{icon}</span>

          <span>{label}</span>
        </span>

        <Tooltip text={tooltip} />
      </label>

      <input
        ref={inputRef}
        className={`decision-field__input ${
          readOnly ? "decision-field__input--readonly" : ""
        }`}
        value={formatNumberDisplay(value, {
          allowDecimal,
        })}
        onChange={readOnly ? undefined : handleChange}
        placeholder={placeholder}
        inputMode={allowDecimal ? "decimal" : "numeric"}
        readOnly={readOnly}
        aria-invalid={Boolean(error)}
        aria-describedby={validationMessage ? messageId : undefined}
      />

      {validationMessage ? (
        <div
          id={messageId}
          className={`decision-field__message ${
            error
              ? "decision-field__message--error"
              : "decision-field__message--warning"
          }`}
          role={error ? "alert" : "status"}
        >
          <AlertTriangle size={14} />

          <span>{validationMessage}</span>
        </div>
      ) : null}
    </div>
  );
}

function TextField({
  icon,
  label,
  tooltip,
  value,
  onChange,
  placeholder,
  readOnly = false,
  error = "",
}) {
  return (
    <div className={`decision-field ${error ? "decision-field--error" : ""}`}>
      <label className="decision-field__label">
        <span className="decision-field__label-main">
          {icon ? <span className="decision-field__icon">{icon}</span> : null}

          <span>{label}</span>
        </span>

        {tooltip ? <Tooltip text={tooltip} /> : null}
      </label>

      <input
        className={`decision-field__input ${
          readOnly ? "decision-field__input--readonly" : ""
        }`}
        type="text"
        value={value}
        onChange={
          readOnly ? undefined : (event) => onChange(event.target.value)
        }
        placeholder={placeholder}
        readOnly={readOnly}
      />

      {error ? (
        <div className="decision-field__message decision-field__message--error">
          <AlertTriangle size={14} />

          <span>{error}</span>
        </div>
      ) : null}
    </div>
  );
}

function SelectField({
  icon,
  label,
  tooltip,
  value,
  onChange,
  placeholder,
  options,
  disabled = false,
  error = "",
  warning = "",
}) {
  const validationMessage = error || warning;

  return (
    <div
      className={`decision-field ${
        error
          ? "decision-field--error"
          : warning
            ? "decision-field--warning"
            : ""
      }`}
    >
      <label className="decision-field__label">
        <span className="decision-field__label-main">
          <span className="decision-field__icon">{icon}</span>

          <span>{label}</span>
        </span>

        <Tooltip text={tooltip} />
      </label>

      <select
        className={`decision-field__input decision-field__select ${
          disabled ? "decision-field__input--readonly" : ""
        }`}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled}
      >
        {placeholder ? <option value="">{placeholder}</option> : null}

        {options.map((option) => (
          <option
            key={option.value}
            value={option.value}
            disabled={Boolean(option.disabled)}
          >
            {option.label}
          </option>
        ))}
      </select>

      {validationMessage ? (
        <div
          className={`decision-field__message ${
            error
              ? "decision-field__message--error"
              : "decision-field__message--warning"
          }`}
        >
          <AlertTriangle size={14} />

          <span>{validationMessage}</span>
        </div>
      ) : null}
    </div>
  );
}

function TextareaField({
  label,
  tooltip,
  value,
  onChange,
  placeholder,
  readOnly = false,
}) {
  return (
    <div className="decision-textarea-wrap">
      <label className="decision-field__label">
        <span className="decision-field__label-main">
          <span>{label}</span>
        </span>

        <Tooltip text={tooltip} />
      </label>

      <textarea
        className={`decision-textarea ${
          readOnly ? "decision-field__input--readonly" : ""
        }`}
        value={value}
        onChange={
          readOnly ? undefined : (event) => onChange(event.target.value)
        }
        placeholder={placeholder}
        readOnly={readOnly}
      />
    </div>
  );
}

function resolveRenovationPlanFileEndpoint(plan) {
  if (!plan) {
    return "";
  }

  if (plan.file_url) {
    if (/^https?:\/\//i.test(plan.file_url)) {
      return plan.file_url;
    }

    return `${API_BASE}${
      plan.file_url.startsWith("/") ? "" : "/"
    }${plan.file_url}`;
  }

  if (plan.project_id && plan.document_id) {
    return (
      `${API_BASE}` +
      `/api/projects/` +
      `${plan.project_id}` +
      `/documents/` +
      `${plan.document_id}` +
      `/file/`
    );
  }

  return "";
}

/* =========================================================
   RENOVATION PLAN MODAL
   ========================================================= */

function RenovationPlanModal({ plan, onClose }) {
  const { t } = useTranslation("lender-submit-decision");

  const { getAccessTokenSilently } = useAuth0();

  const [previewUrl, setPreviewUrl] = React.useState("");

  const [isLoading, setIsLoading] = React.useState(true);

  const [isDownloading, setIsDownloading] = React.useState(false);

  const [error, setError] = React.useState("");

  const previewRef = React.useRef("");

  const fileEndpoint = resolveRenovationPlanFileEndpoint(plan);

  React.useEffect(() => {
    const previous = document.body.style.overflow;

    document.body.style.overflow = "hidden";

    function handleKeyDown(event) {
      if (event.key === "Escape") {
        onClose();
      }
    }

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previous;

      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose]);

  React.useEffect(() => {
    if (!fileEndpoint) {
      setIsLoading(false);

      setError(
        t(
          "renovationPlan.fileUnavailable",
          "The renovation plan file is not available.",
        ),
      );

      return;
    }

    const controller = new AbortController();

    async function loadFile() {
      try {
        setIsLoading(true);
        setError("");

        const token = await getAccessTokenSilently();

        const response = await fetch(`${fileEndpoint}?disposition=inline`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },

          signal: controller.signal,
        });

        if (!response.ok) {
          const data = await response.json().catch(() => ({}));

          throw new Error(
            data?.detail ||
              t(
                "renovationPlan.loadFailed",
                "The renovation plan could not be loaded.",
              ),
          );
        }

        const blob = await response.blob();

        const objectUrl = URL.createObjectURL(blob);

        if (previewRef.current) {
          URL.revokeObjectURL(previewRef.current);
        }

        previewRef.current = objectUrl;

        setPreviewUrl(objectUrl);
      } catch (err) {
        if (err?.name === "AbortError") {
          return;
        }

        setError(
          err?.message ||
            t(
              "renovationPlan.loadFailed",
              "The renovation plan could not be loaded.",
            ),
        );
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      }
    }

    loadFile();

    return () => {
      controller.abort();

      if (previewRef.current) {
        URL.revokeObjectURL(previewRef.current);
      }
    };
  }, [fileEndpoint, getAccessTokenSilently, t]);

  async function downloadPlan() {
    if (!fileEndpoint || isDownloading) {
      return;
    }

    try {
      setIsDownloading(true);

      const token = await getAccessTokenSilently();

      const response = await fetch(`${fileEndpoint}?disposition=attachment`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));

        throw new Error(
          data?.detail ||
            t(
              "renovationPlan.downloadFailed",
              "The renovation plan could not be downloaded.",
            ),
        );
      }

      const blob = await response.blob();

      const objectUrl = URL.createObjectURL(blob);

      const anchor = document.createElement("a");

      anchor.href = objectUrl;

      anchor.download = plan?.filename || "renovation-plan.pdf";

      document.body.appendChild(anchor);

      anchor.click();
      anchor.remove();

      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      setError(
        err?.message ||
          t(
            "renovationPlan.downloadFailed",
            "The renovation plan could not be downloaded.",
          ),
      );
    } finally {
      setIsDownloading(false);
    }
  }

  return (
    <div
      className="decision-plan-modal__backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="decision-plan-modal" role="dialog" aria-modal="true">
        <div className="decision-plan-modal__header">
          <div className="decision-plan-modal__heading">
            <div className="decision-plan-modal__icon">
              <FileText size={21} />
            </div>

            <div>
              <h2>{t("renovationPlan.title", "Renovation plan")}</h2>

              <p>{plan?.filename || "Renovation plan"}</p>
            </div>
          </div>

          <button
            type="button"
            className="decision-plan-modal__close"
            onClick={onClose}
          >
            <X size={19} />
          </button>
        </div>

        <div className="decision-plan-modal__meta">
          {plan?.project_code ? (
            <span>
              {t("renovationPlan.project", "Project")}:{" "}
              <strong>{plan.project_code}</strong>
            </span>
          ) : null}

          {plan?.size_bytes ? (
            <span>{formatFileSize(plan.size_bytes)}</span>
          ) : null}

          {plan?.uploaded_at ? (
            <span>
              {t("renovationPlan.uploaded", "Uploaded")}:{" "}
              {formatDocumentDate(plan.uploaded_at)}
            </span>
          ) : null}
        </div>

        <div className="decision-plan-modal__body">
          {isLoading ? (
            <div className="decision-plan-modal__state">
              <LoaderCircle
                size={28}
                className="decision-plan-modal__spinner"
              />

              <span>
                {t("renovationPlan.loading", "Loading renovation plan...")}
              </span>
            </div>
          ) : error ? (
            <div className="decision-plan-modal__state decision-plan-modal__state--error">
              <AlertTriangle size={26} />

              <span>{error}</span>
            </div>
          ) : (
            <iframe
              src={previewUrl}
              title={plan?.filename || "Renovation plan"}
              className="decision-plan-modal__frame"
            />
          )}
        </div>

        <div className="decision-plan-modal__footer">
          <p>
            {t(
              "renovationPlan.modalHelper",
              "Review the renovation plan before confirming the renovation financing amount.",
            )}
          </p>

          <button type="button" onClick={downloadPlan} disabled={isDownloading}>
            {isDownloading ? (
              <LoaderCircle
                size={16}
                className="decision-plan-modal__spinner"
              />
            ) : (
              <Download size={16} />
            )}

            {isDownloading
              ? t("renovationPlan.downloading", "Downloading...")
              : t("renovationPlan.download", "Download")}
          </button>
        </div>
      </div>
    </div>
  );
}

/* =========================================================
   VALIDATION
   ========================================================= */

function buildApprovalValidation({
  grantForm,
  financingDetails,
  fundingSources,
  renovationFinancingStatus,
  hasRenovationPlan,
  countryCode,
  t,
}) {
  const fieldErrors = {};
  const fieldWarnings = {};

  const fundingSourceErrors = {};

  const errorMessages = [];
  const warningMessages = [];

  const addError = (fields, message) => {
    const targetFields = Array.isArray(fields) ? fields : [fields];

    targetFields.forEach((field) => {
      if (!fieldErrors[field]) {
        fieldErrors[field] = message;
      }
    });

    if (!errorMessages.includes(message)) {
      errorMessages.push(message);
    }
  };

  const addWarning = (fields, message) => {
    const targetFields = Array.isArray(fields) ? fields : [fields];

    targetFields.forEach((field) => {
      if (!fieldWarnings[field]) {
        fieldWarnings[field] = message;
      }
    });

    if (!warningMessages.includes(message)) {
      warningMessages.push(message);
    }
  };

  function addFundingError(index, field, message) {
    if (!fundingSourceErrors[index]) {
      fundingSourceErrors[index] = {};
    }

    fundingSourceErrors[index][field] = message;

    if (!errorMessages.includes(message)) {
      errorMessages.push(message);
    }
  }

  const details = financingDetails || {};

  const propertyPrice = toNumericValue(details.property_purchase_price);

  const initialRenovationEstimate = toNumericValue(
    details.estimated_renovation_budget,
  );

  const requestedPurchaseLoan = toNumericValue(
    details.requested_purchase_loan_amount,
  );

  const requestedRenovationLoan = toNumericValue(
    details.requested_renovation_loan_amount,
  );

  const requestedTotal = toNumericValue(
    details.requested_total_financing_amount,
  );

  const approvedTotal = toNumericValue(grantForm.approved_loan_amount);

  const approvedPurchase = toNumericValue(grantForm.purchase_loan_amount);

  const approvedRenovation = toNumericValue(grantForm.renovation_loan_amount);

  const ownContribution = toNumericValue(grantForm.own_contribution);

  const tenure = toNumericValue(grantForm.tenure_years);

  const apr = toNumericValue(grantForm.indicative_apr);

  const monthlyPayment = toNumericValue(grantForm.estimated_monthly_payment);

  const mortgageAmount = toNumericValue(grantForm.mortgage_amount);

  const mortgageMandate = toNumericValue(grantForm.mortgage_mandate_amount);

  /* -------------------------------------------------------
     Lender total
     ------------------------------------------------------- */

  if (
    hasNumericInput(grantForm.approved_loan_amount) &&
    !valuesAreEqual(approvedTotal, approvedPurchase + approvedRenovation)
  ) {
    addError(
      [
        "approved_loan_amount",
        "purchase_loan_amount",
        "renovation_loan_amount",
      ],
      t("validation.approvedTotalMismatch", {
        calculated: formatCurrencyDisplay(
          approvedPurchase + approvedRenovation,
        ),

        approved: formatCurrencyDisplay(approvedTotal),

        defaultValue:
          "The total lender loan must equal the purchase loan plus the renovation loan.",
      }),
    );
  }

  /* -------------------------------------------------------
     Purchase financing remains tied to the purchase request
     ------------------------------------------------------- */

  if (propertyPrice > 0 && approvedPurchase > propertyPrice) {
    addError(
      "purchase_loan_amount",
      t("validation.purchaseLoanExceedsPrice", {
        amount: formatCurrencyDisplay(propertyPrice),

        defaultValue:
          "The purchase loan cannot exceed the property purchase price.",
      }),
    );
  }

  if (requestedPurchaseLoan > 0 && approvedPurchase > requestedPurchaseLoan) {
    addError(
      "purchase_loan_amount",
      t("validation.purchaseLoanExceedsRequest", {
        amount: formatCurrencyDisplay(requestedPurchaseLoan),

        defaultValue:
          "The purchase loan exceeds the originally requested purchase financing.",
      }),
    );
  }

  /* -------------------------------------------------------
     Renovation values are references, not hard caps.
     ------------------------------------------------------- */

  if (
    requestedRenovationLoan > 0 &&
    approvedRenovation > requestedRenovationLoan
  ) {
    addWarning(
      "renovation_loan_amount",
      t("validation.renovationAboveOriginalRequest", {
        amount: formatCurrencyDisplay(requestedRenovationLoan),

        defaultValue:
          "The renovation loan exceeds the original renovation-financing request. Confirm that the revised amount reflects the latest renovation information.",
      }),
    );
  }

  if (
    initialRenovationEstimate > 0 &&
    approvedRenovation > initialRenovationEstimate
  ) {
    addWarning(
      "renovation_loan_amount",
      t("validation.renovationAboveInitialEstimate", {
        amount: formatCurrencyDisplay(initialRenovationEstimate),

        defaultValue:
          "The renovation loan exceeds the initial renovation estimate. This may be valid if the detailed renovation plan contains revised costs.",
      }),
    );
  }

  if (requestedTotal > 0 && approvedTotal > requestedTotal) {
    addWarning(
      "approved_loan_amount",
      t("validation.totalAboveOriginalRequest", {
        defaultValue:
          "The total lender financing exceeds the original request. Verify that the difference results from revised renovation financing.",
      }),
    );
  }

  /* -------------------------------------------------------
     Renovation financing maturity
     ------------------------------------------------------- */

  if (renovationFinancingStatus === "CONFIRMED" && !hasRenovationPlan) {
    addError(
      "renovation_financing_status",
      t(
        "validation.confirmedRequiresPlan",
        "Renovation financing can only be marked as confirmed when the renovation plan is available.",
      ),
    );
  }

  if (hasRenovationPlan && renovationFinancingStatus === "ESTIMATED") {
    addWarning(
      "renovation_financing_status",
      t(
        "validation.planAvailableStillEstimated",
        "A renovation plan is available. Review it before deciding whether the renovation financing can be marked as confirmed.",
      ),
    );
  }

  /* -------------------------------------------------------
     Tenure / APR
     ------------------------------------------------------- */

  if (
    hasNumericInput(grantForm.tenure_years) &&
    (tenure <= 0 || !Number.isInteger(tenure))
  ) {
    addError(
      "tenure_years",
      t(
        "validation.invalidTenure",
        "The tenure must be a positive whole number.",
      ),
    );
  }

  if (hasNumericInput(grantForm.indicative_apr) && apr < 0) {
    addError(
      "indicative_apr",
      t("validation.invalidApr", "APR cannot be negative."),
    );
  }

  /* -------------------------------------------------------
     Belgium mortgage security
     ------------------------------------------------------- */

  if (
    countryCode === "BE" &&
    (hasNumericInput(grantForm.mortgage_amount) ||
      hasNumericInput(grantForm.mortgage_mandate_amount))
  ) {
    const securityTotal = mortgageAmount + mortgageMandate;

    if (!valuesAreEqual(securityTotal, approvedTotal)) {
      addError(
        ["mortgage_amount", "mortgage_mandate_amount"],
        t("validation.mortgageSplitMismatch", {
          securityTotal: formatCurrencyDisplay(securityTotal),

          approvedTotal: formatCurrencyDisplay(approvedTotal),

          defaultValue:
            "The mortgage amount and mortgage mandate must together equal the total lender loan.",
        }),
      );
    }
  }

  /* -------------------------------------------------------
     Monthly payment
     ------------------------------------------------------- */

  if (
    approvedTotal > 0 &&
    tenure > 0 &&
    apr >= 0 &&
    hasNumericInput(grantForm.estimated_monthly_payment)
  ) {
    const calculated = calculateMonthlyPayment(approvedTotal, apr, tenure);

    if (calculated !== null) {
      const allowedDifference = Math.max(1, calculated * 0.02);

      if (Math.abs(monthlyPayment - calculated) > allowedDifference) {
        addWarning(
          "estimated_monthly_payment",
          t("validation.monthlyPaymentMismatch", {
            entered: formatCurrencyDisplay(monthlyPayment),

            calculated: formatCurrencyDisplay(calculated),

            defaultValue:
              "The entered monthly payment differs from the indicative calculation.",
          }),
        );
      }
    }
  }

  /* -------------------------------------------------------
     External funding sources
     ------------------------------------------------------- */

  fundingSources.forEach((source, index) => {
    const hasAnyValue = Boolean(
      source.source_type || source.name || source.amount,
    );

    if (!hasAnyValue) {
      return;
    }

    if (!source.source_type) {
      addFundingError(
        index,
        "source_type",
        t("validation.fundingTypeRequired", "Select the funding type."),
      );
    }

    if (!String(source.name || "").trim()) {
      addFundingError(
        index,
        "name",
        t("validation.fundingNameRequired", "Enter the funding source name."),
      );
    }

    if (!hasNumericInput(source.amount) || toNumericValue(source.amount) <= 0) {
      addFundingError(
        index,
        "amount",
        t(
          "validation.fundingAmountRequired",
          "Enter a funding amount greater than zero.",
        ),
      );
    }

    if (!source.status) {
      addFundingError(
        index,
        "status",
        t(
          "validation.fundingStatusRequired",
          "Select whether the funding is expected or confirmed.",
        ),
      );
    }
  });

  return {
    fieldErrors,
    fieldWarnings,
    fundingSourceErrors,
    errorMessages,
    warningMessages,

    hasBlockingErrors: errorMessages.length > 0,

    hasWarnings: warningMessages.length > 0,
  };
}

/* =========================================================
   PAGE
   ========================================================= */

export default function LenderSubmitDecisionPage() {
  const { t } = useTranslation("lender-submit-decision");

  const navigate = useNavigate();

  const { loanCaseId } = useParams();

  const { setFormDirty, requestNavigation } = useOutletContext() || {};

  const { getAccessTokenSilently, isAuthenticated } = useAuth0();

  const { showToast } = useToast();

  /* =======================================================
     STATE
     ======================================================= */

  const [decision, setDecision] = React.useState("APPROVED");

  const [grantForm, setGrantForm] = React.useState(EMPTY_GRANT_FORM);

  const [rejectForm, setRejectForm] = React.useState(EMPTY_CLOSE_FORM);

  const [fundingSources, setFundingSources] = React.useState([]);

  const [renovationFinancingStatus, setRenovationFinancingStatus] =
    React.useState("ESTIMATED");

  const [renovationFinancingNotes, setRenovationFinancingNotes] =
    React.useState("");

  const [selectedRenovationPlan, setSelectedRenovationPlan] =
    React.useState(null);

  const [loanCaseData, setLoanCaseData] = React.useState(null);

  const [isLoadingCase, setIsLoadingCase] = React.useState(true);

  const [caseError, setCaseError] = React.useState("");

  const [monthlyPaymentOverridden, setMonthlyPaymentOverridden] =
    React.useState(false);

  const [hasUnsavedChanges, setHasUnsavedChanges] = React.useState(false);

  const [showSubmitConfirmation, setShowSubmitConfirmation] =
    React.useState(false);

  const [isSavingDecision, setIsSavingDecision] = React.useState(false);

  const fundingCounter = React.useRef(0);

  /* =======================================================
     DIRTY STATE
     ======================================================= */

  React.useEffect(() => {
    setFormDirty?.(hasUnsavedChanges);
  }, [hasUnsavedChanges, setFormDirty]);

  React.useEffect(() => {
    return () => {
      setFormDirty?.(false);
    };
  }, [setFormDirty]);

  /* =======================================================
     NAVIGATION
     ======================================================= */

  function handleBackToLoanCases() {
    if (requestNavigation) {
      requestNavigation("/lender/loan-cases");

      return;
    }

    navigate("/lender/loan-cases");
  }

  /* =======================================================
     DERIVED BACKEND DATA
     ======================================================= */

  const loanCase = loanCaseData?.loan_case;

  const dossier = loanCaseData?.dossier;

  const applicant = loanCaseData?.primary_applicant;

  const property = loanCaseData?.property;

  const internalRenovationPlan = loanCaseData?.renovation_plan || null;

  const externalRenovation = loanCaseData?.external_renovation || null;

  const externalRenovationPlan = externalRenovation?.plan || null;

  const isExternalRenovation = Boolean(
    externalRenovation?.available || externalRenovation?.id,
  );

  const hasInternalRenovationPlan = Boolean(
    internalRenovationPlan &&
    resolveRenovationPlanFileEndpoint(internalRenovationPlan),
  );

  const hasExternalRenovationPlan = Boolean(
    externalRenovationPlan &&
    resolveRenovationPlanFileEndpoint(externalRenovationPlan),
  );

  const renovationPlan = hasInternalRenovationPlan
    ? {
        ...internalRenovationPlan,
        source: internalRenovationPlan?.source || "LEGUP_OSS",
      }
    : hasExternalRenovationPlan
      ? {
          ...externalRenovationPlan,
          source: "EXTERNAL_OSS",
        }
      : null;

  const hasRenovationPlan = Boolean(renovationPlan);

  const isExternalRenovationPlan = renovationPlan?.source === "EXTERNAL_OSS";

  const marketRegionCode = (
    dossier?.market_region?.code ||
    dossier?.market_region_code ||
    dossier?.region ||
    ""
  )
    .trim()
    .toUpperCase();

  const marketRegionName =
    dossier?.market_region?.name ||
    dossier?.market_region_name ||
    dossier?.region_label ||
    dossier?.region ||
    "—";

  const isFlandersCase =
    dossier?.country_code === "BE" && marketRegionCode === "FLANDERS";

  /* =======================================================
     DECISION MODE
     ======================================================= */

  const decisionMode =
    loanCase?.decision_mode ||
    (loanCaseData?.result?.is_draft
      ? "CONTINUE_DRAFT"
      : loanCaseData?.result?.decision === "APPROVED"
        ? "EDIT_APPROVED"
        : loanCaseData?.result?.decision === "REJECTED"
          ? "VIEW_REJECTED"
          : "CREATE");

  const isExistingApproval = decisionMode === "EDIT_APPROVED";

  const isRejectedView = decisionMode === "VIEW_REJECTED";

  const isDecisionLocked = isExistingApproval || isRejectedView;

  const canSaveDraft = !isExistingApproval && !isRejectedView;

  const originalRenovationStatus =
    loanCaseData?.result?.renovation_financing_status || "ESTIMATED";

  const renovationWasConfirmed = originalRenovationStatus === "CONFIRMED";

  /* =======================================================
     LOAD CASE
     ======================================================= */

  React.useEffect(() => {
    if (!isAuthenticated || !loanCaseId) {
      setIsLoadingCase(false);

      return;
    }

    const controller = new AbortController();

    async function loadLoanCase() {
      try {
        setIsLoadingCase(true);

        setCaseError("");

        const token = await getAccessTokenSilently();

        const response = await fetch(
          `${API_BASE}/api/lender/loan-cases/${loanCaseId}/`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },

            signal: controller.signal,
          },
        );

        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(data.detail || t("errors.loadCaseFailed"));
        }

        setLoanCaseData(data);

        if (data.result) {
          setDecision(
            data.result.decision === "REJECTED" ? "REJECTED" : "APPROVED",
          );

          setGrantForm({
            approved_loan_amount: normalizeApiNumericValue(
              data.result.approved_total_loan_amount,
            ),

            purchase_loan_amount: normalizeApiNumericValue(
              data.result.approved_purchase_loan_amount,
            ),

            renovation_loan_amount: normalizeApiNumericValue(
              data.result.approved_renovation_loan_amount,
            ),

            own_contribution: normalizeApiNumericValue(
              data.result.own_contribution_amount ??
                data.financing_details?.own_contribution_amount,
            ),

            tenure_years: normalizeApiNumericValue(data.result.tenure_years),

            indicative_apr: normalizeApiNumericValue(
              data.result.indicative_apr_pct,
              {
                allowDecimal: true,
              },
            ),

            estimated_monthly_payment: normalizeApiNumericValue(
              data.result.estimated_monthly_payment,
              {
                allowDecimal: true,
              },
            ),

            mortgage_amount: normalizeApiNumericValue(
              data.result.mortgage_amount,
            ),

            mortgage_mandate_amount: normalizeApiNumericValue(
              data.result.mortgage_mandate_amount,
            ),

            conditions: data.result.conditions || "",
          });

          setRejectForm({
            primary_rejection_reason: data.result.rejection_reason_code || "",

            details_explanation: data.result.rejection_details || "",

            allow_reapplication:
              data.result.allow_reapplication === true
                ? "YES"
                : data.result.allow_reapplication === false
                  ? "NO"
                  : "",

            reapply_after: data.result.reapply_after || "",
          });

          setRenovationFinancingStatus(
            data.result.renovation_financing_status || "ESTIMATED",
          );

          setRenovationFinancingNotes(
            data.result.renovation_financing_notes || "",
          );

          setFundingSources(
            (data.result.funding_sources || []).map((source, index) => ({
              client_key: source.id
                ? `existing-${source.id}`
                : `existing-${index}`,

              id: source.id || null,

              source_type: source.source_type || "",

              name: source.name || "",

              amount: normalizeApiNumericValue(source.amount),

              status: source.status || "EXPECTED",
            })),
          );
        } else {
          const financing = data.financing_details || {};

          const purchaseLoan = normalizeApiNumericValue(
            financing.requested_purchase_loan_amount,
          );

          const renovationLoan = normalizeApiNumericValue(
            financing.requested_renovation_loan_amount,
          );

          const total =
            toNumericValue(purchaseLoan) + toNumericValue(renovationLoan);

          setGrantForm({
            ...EMPTY_GRANT_FORM,

            approved_loan_amount: total > 0 ? String(total) : "",

            purchase_loan_amount: purchaseLoan,

            renovation_loan_amount: renovationLoan,

            own_contribution: normalizeApiNumericValue(
              financing.own_contribution_amount,
            ),

            tenure_years: normalizeApiNumericValue(financing.tenure_years),
          });

          setFundingSources([]);

          setRenovationFinancingStatus("ESTIMATED");

          setRenovationFinancingNotes("");
        }

        setHasUnsavedChanges(false);
      } catch (error) {
        if (error.name === "AbortError") {
          return;
        }

        console.error("Failed to load lender loan case", error);

        setCaseError(error.message || t("errors.loadCaseFailed"));
      } finally {
        if (!controller.signal.aborted) {
          setIsLoadingCase(false);
        }
      }
    }

    loadLoanCase();

    return () => controller.abort();
  }, [getAccessTokenSilently, isAuthenticated, loanCaseId, t]);

  /* =======================================================
     UPDATERS
     ======================================================= */

  const updateGrant = (field) => (value) => {
    if (isRejectedView) {
      return;
    }

    setHasUnsavedChanges(true);

    const recalculationFields = [
      "purchase_loan_amount",
      "renovation_loan_amount",
      "tenure_years",
      "indicative_apr",
    ];

    if (recalculationFields.includes(field)) {
      setMonthlyPaymentOverridden(false);
    }

    setGrantForm((previous) => {
      const next = {
        ...previous,
        [field]: value,
      };

      if (
        field === "purchase_loan_amount" ||
        field === "renovation_loan_amount"
      ) {
        const purchase = toNumericValue(next.purchase_loan_amount);

        const renovation = toNumericValue(next.renovation_loan_amount);

        const total = purchase + renovation;

        next.approved_loan_amount = total > 0 ? String(total) : "";
      }

      if (recalculationFields.includes(field)) {
        const calculated = calculateMonthlyPayment(
          next.approved_loan_amount,
          next.indicative_apr,
          next.tenure_years,
        );

        next.estimated_monthly_payment =
          calculated === null ? "" : calculated.toFixed(2);
      }

      return next;
    });
  };

  const updateReject = (field) => (value) => {
    if (isRejectedView) {
      return;
    }

    setHasUnsavedChanges(true);

    setRejectForm((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  function updateRenovationStatus(value) {
    if (isRejectedView || renovationWasConfirmed) {
      return;
    }

    setRenovationFinancingStatus(value);

    setHasUnsavedChanges(true);
  }

  function updateRenovationNotes(value) {
    if (isRejectedView) {
      return;
    }

    setRenovationFinancingNotes(value);

    setHasUnsavedChanges(true);
  }

  /* =======================================================
     FUNDING SOURCES
     ======================================================= */

  function getDefaultFundingName(sourceType) {
    if (isFlandersCase && sourceType === "SUBSIDY") {
      return "Mijn VerbouwPremie";
    }

    if (isFlandersCase && sourceType === "SOFT_LOAN") {
      return "Mijn Verbouwlening";
    }

    return "";
  }

  function addFundingSource() {
    if (isRejectedView) {
      return;
    }

    fundingCounter.current += 1;

    setFundingSources((previous) => [
      ...previous,

      {
        client_key: `new-${fundingCounter.current}`,

        id: null,

        source_type: "SUBSIDY",

        name: getDefaultFundingName("SUBSIDY"),

        amount: "",

        status: "EXPECTED",
      },
    ]);

    setHasUnsavedChanges(true);
  }

  function updateFundingSource(index, field, value) {
    if (isRejectedView) {
      return;
    }

    setFundingSources((previous) =>
      previous.map((source, sourceIndex) => {
        if (sourceIndex !== index) {
          return source;
        }

        if (field === "source_type") {
          const knownNames = ["", "Mijn VerbouwPremie", "Mijn Verbouwlening"];

          return {
            ...source,

            source_type: value,

            name: knownNames.includes(source.name)
              ? getDefaultFundingName(value)
              : source.name,
          };
        }

        return {
          ...source,
          [field]: value,
        };
      }),
    );

    setHasUnsavedChanges(true);
  }

  function removeFundingSource(index) {
    if (isRejectedView) {
      return;
    }

    setFundingSources((previous) =>
      previous.filter((_, sourceIndex) => sourceIndex !== index),
    );

    setHasUnsavedChanges(true);
  }

  /* =======================================================
     CONTEXT
     ======================================================= */

  const propertyAddress = formatPropertyAddress(
    property,
    dossier?.country_label,
  );

  const sourceLabel =
    loanCase?.source === "REFERENCE_IMPORT"
      ? t("context.dossierReference")
      : t("context.manualCase");

  /* =======================================================
     VALIDATION
     ======================================================= */

  const approvalValidation = React.useMemo(
    () =>
      buildApprovalValidation({
        grantForm,

        financingDetails: {
          ...(loanCaseData?.financing_details || {}),

          property_purchase_price:
            loanCaseData?.financing_details?.property_purchase_price ??
            (loanCase?.source === "MANUAL_ENTRY"
              ? loanCaseData?.result?.property_price
              : null),

          tenure_years:
            loanCaseData?.financing_details?.tenure_years ??
            (loanCase?.source === "MANUAL_ENTRY"
              ? loanCaseData?.result?.tenure_years
              : null),
        },

        fundingSources,

        renovationFinancingStatus,

        hasRenovationPlan,

        countryCode: dossier?.country_code,

        t,
      }),

    [
      grantForm,
      fundingSources,
      renovationFinancingStatus,
      hasRenovationPlan,
      loanCaseData?.financing_details,
      dossier?.country_code,
      t,
    ],
  );

  function emptyToNull(value) {
    if (value === null || value === undefined) {
      return null;
    }

    const normalized = String(value).trim();

    return normalized === "" ? null : normalized;
  }

  /* =======================================================
     PAYLOAD
     ======================================================= */

  function buildDecisionPayload(isDraft) {
    if (decision === "APPROVED") {
      return {
        decision: "APPROVED",

        is_draft: isDraft,

        approved_total_loan_amount: emptyToNull(grantForm.approved_loan_amount),

        approved_purchase_loan_amount: emptyToNull(
          grantForm.purchase_loan_amount,
        ),

        approved_renovation_loan_amount: emptyToNull(
          grantForm.renovation_loan_amount,
        ),

        renovation_financing_status: renovationFinancingStatus,

        renovation_financing_notes: renovationFinancingNotes.trim(),

        own_contribution_amount: emptyToNull(grantForm.own_contribution),

        tenure_years: emptyToNull(grantForm.tenure_years),

        indicative_apr_pct: emptyToNull(grantForm.indicative_apr),

        estimated_monthly_payment: emptyToNull(
          grantForm.estimated_monthly_payment,
        ),

        mortgage_amount: emptyToNull(grantForm.mortgage_amount),

        mortgage_mandate_amount: emptyToNull(grantForm.mortgage_mandate_amount),

        conditions: grantForm.conditions.trim(),

        funding_sources: fundingSources
          .filter((source) =>
            Boolean(source.source_type || source.name || source.amount),
          )
          .map((source) => ({
            source_type: source.source_type,

            name: String(source.name || "").trim(),

            amount: emptyToNull(source.amount),

            status: source.status || "EXPECTED",
          })),

        rejection_reason_code: "",

        rejection_details: "",

        allow_reapplication: null,

        reapply_after: "",
      };
    }

    return {
      decision: "REJECTED",

      is_draft: isDraft,

      rejection_reason_code: rejectForm.primary_rejection_reason || "",

      rejection_details: rejectForm.details_explanation.trim(),

      allow_reapplication:
        rejectForm.allow_reapplication === "YES"
          ? true
          : rejectForm.allow_reapplication === "NO"
            ? false
            : null,

      reapply_after:
        rejectForm.allow_reapplication === "YES"
          ? rejectForm.reapply_after
          : "",

      approved_total_loan_amount: null,

      approved_purchase_loan_amount: null,

      approved_renovation_loan_amount: null,

      own_contribution_amount: null,

      tenure_years: null,

      indicative_apr_pct: null,

      estimated_monthly_payment: null,

      mortgage_amount: null,

      mortgage_mandate_amount: null,

      conditions: "",

      funding_sources: [],
    };
  }

  /* =======================================================
     FINAL VALIDATION
     ======================================================= */

  function validateFinalDecision() {
    if (decision === "APPROVED") {
      const requiredChecks = [
        [
          grantForm.approved_loan_amount,
          t(
            "toast.approvedAmountRequired",
            "Enter the approved lender loan amount.",
          ),
        ],

        [
          grantForm.purchase_loan_amount,
          t(
            "validation.purchaseLoanRequired",
            "Enter the approved purchase-loan amount.",
          ),
        ],

        [
          grantForm.renovation_loan_amount,
          t(
            "validation.renovationLoanRequired",
            "Enter the approved renovation-loan amount.",
          ),
        ],

        [
          grantForm.own_contribution,
          t(
            "validation.ownContributionRequired",
            "Enter the homebuyer's own contribution.",
          ),
        ],

        [
          grantForm.tenure_years,
          t("toast.tenureRequired", "Enter the loan tenure."),
        ],

        [
          grantForm.indicative_apr,
          t("validation.aprRequired", "Enter the indicative APR."),
        ],

        [
          grantForm.estimated_monthly_payment,
          t(
            "validation.monthlyPaymentRequired",
            "Enter the estimated monthly payment.",
          ),
        ],
      ];

      const missing = requiredChecks.find(([value]) => !hasNumericInput(value));

      if (missing) {
        showToast({
          type: "error",

          title: t("toast.validationErrorTitle"),

          message: missing[1],
        });

        return false;
      }

      if (approvalValidation.hasBlockingErrors) {
        showToast({
          type: "error",

          title: t("toast.validationErrorTitle"),

          message: approvalValidation.errorMessages[0],
        });

        return false;
      }
    }

    if (decision === "REJECTED") {
      if (!rejectForm.primary_rejection_reason) {
        showToast({
          type: "error",

          title: t("toast.validationErrorTitle"),

          message: t("toast.rejectionReasonRequired"),
        });

        return false;
      }

      if (!rejectForm.details_explanation.trim()) {
        showToast({
          type: "error",

          title: t("toast.validationErrorTitle"),

          message: t(
            "validation.rejectionDetailsRequired",
            "Provide an explanation for the rejection.",
          ),
        });

        return false;
      }

      if (!rejectForm.allow_reapplication) {
        showToast({
          type: "error",

          title: t("toast.validationErrorTitle"),

          message: t("toast.allowReapplicationRequired"),
        });

        return false;
      }

      if (
        rejectForm.allow_reapplication === "YES" &&
        !rejectForm.reapply_after
      ) {
        showToast({
          type: "error",

          title: t("toast.validationErrorTitle"),

          message: t("toast.reapplyAfterRequired"),
        });

        return false;
      }
    }

    return true;
  }

  /* =======================================================
     SAVE
     ======================================================= */

  async function handleDecisionSave(isDraft) {
    if (!loanCaseId || isSavingDecision || isRejectedView) {
      return;
    }

    if (isExistingApproval && isDraft) {
      return;
    }

    if (!isDraft && !validateFinalDecision()) {
      return;
    }

    try {
      setIsSavingDecision(true);

      const token = await getAccessTokenSilently();

      const payload = buildDecisionPayload(isDraft);

      const response = await fetch(
        `${API_BASE}/api/lender/loan-cases/${loanCaseId}/decision/`,
        {
          method: "POST",

          headers: {
            Authorization: `Bearer ${token}`,

            "Content-Type": "application/json",
          },

          body: JSON.stringify(payload),
        },
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          data.detail || data.message || t("errors.saveDecisionFailed"),
        );
      }

      setLoanCaseData((previous) => {
        if (!previous) {
          return previous;
        }

        return {
          ...previous,

          result: data.result || previous.result,

          loan_case: {
            ...previous.loan_case,

            ...(data.loan_case || {}),
          },
        };
      });

      setHasUnsavedChanges(false);

      setFormDirty?.(false);

      if (isDraft) {
        showToast({
          type: "success",

          title: t("toast.draftSavedTitle"),

          message: t("toast.draftSavedMessage"),
        });

        return;
      }

      if (isExistingApproval) {
        showToast({
          type: "success",

          title: t("toast.financingUpdatedTitle", "Financing updated"),

          message: t(
            "toast.financingUpdatedMessage",
            "The approved financing information was updated successfully.",
          ),
        });
      } else {
        showToast({
          type: "success",

          title: t("toast.decisionSubmittedTitle"),

          message:
            decision === "APPROVED"
              ? t("toast.grantSubmittedMessage")
              : t("toast.rejectionSubmittedMessage"),
        });
      }

      navigate("/lender/loan-cases", {
        replace: true,

        state: {
          decisionSubmitted: true,

          loanCaseId,
        },
      });
    } catch (error) {
      console.error("Failed to save lender decision", error);

      showToast({
        type: "error",

        title: t("toast.saveFailedTitle"),

        message: error.message || t("errors.saveDecisionFailed"),
      });
    } finally {
      setIsSavingDecision(false);
    }
  }

  /* =======================================================
     MONTHLY PAYMENT
     ======================================================= */

  function recalculateMonthlyPayment() {
    if (isRejectedView) {
      return;
    }

    const calculated = calculateMonthlyPayment(
      grantForm.approved_loan_amount,

      grantForm.indicative_apr,

      grantForm.tenure_years,
    );

    if (calculated === null) {
      return;
    }

    setGrantForm((previous) => ({
      ...previous,

      estimated_monthly_payment: calculated.toFixed(2),
    }));

    setMonthlyPaymentOverridden(false);

    setHasUnsavedChanges(true);
  }

  /* =======================================================
     SUMMARY
     ======================================================= */

  const decisionSummary = React.useMemo(() => {
    const approvedTotal = toNumericValue(grantForm.approved_loan_amount);

    const approvedPurchase = toNumericValue(grantForm.purchase_loan_amount);

    const approvedRenovation = toNumericValue(grantForm.renovation_loan_amount);

    const propertyPrice = toNumericValue(
      loanCaseData?.financing_details?.property_purchase_price,
    );

    const initialRenovationEstimate = toNumericValue(
      loanCaseData?.financing_details?.estimated_renovation_budget,
    );

    const externalFunding = fundingSources.reduce(
      (total, source) => total + toNumericValue(source.amount),
      0,
    );

    const totalRenovationFunding = approvedRenovation + externalFunding;

    const renovationFundingBalance =
      totalRenovationFunding - initialRenovationEstimate;

    const purchaseLtv =
      propertyPrice > 0 ? (approvedPurchase / propertyPrice) * 100 : null;

    const referenceProjectCost = propertyPrice + initialRenovationEstimate;

    const lenderToProjectCost =
      referenceProjectCost > 0
        ? (approvedTotal / referenceProjectCost) * 100
        : null;

    return {
      approvedTotal,
      approvedPurchase,
      approvedRenovation,

      initialRenovationEstimate,

      externalFunding,

      totalRenovationFunding,

      renovationFundingBalance,

      purchaseLtv,

      lenderToProjectCost,

      securityTotal:
        toNumericValue(grantForm.mortgage_amount) +
        toNumericValue(grantForm.mortgage_mandate_amount),
    };
  }, [grantForm, fundingSources, loanCaseData?.financing_details]);

  /* =======================================================
     LOADING / ERROR
     ======================================================= */

  if (isLoadingCase) {
    return (
      <div className="lender-submit-decision__state">
        <div className="decision-page-spinner" />

        <p>{t("loading.case")}</p>
      </div>
    );
  }

  if (caseError) {
    return (
      <div className="lender-submit-decision__state">
        <AlertTriangle size={24} />

        <h2>{t("errors.loadCaseTitle")}</h2>

        <p>{caseError}</p>

        <button type="button" onClick={() => navigate("/lender/loan-cases")}>
          {t("breadcrumb.loanCases")}
        </button>
      </div>
    );
  }

  /* =======================================================
   RENDER
   ======================================================= */

  return (
    <div className="lender-submit-decision">
      <div className="submit-decision__breadcrumb">
        <button
          type="button"
          className="submit-decision__breadcrumb-link"
          onClick={handleBackToLoanCases}
        >
          <ArrowLeft size={15} />

          {t("breadcrumb.loanCases")}
        </button>

        <ChevronRight
          size={15}
          className="submit-decision__breadcrumb-separator"
        />

        <span className="submit-decision__breadcrumb-current">
          {loanCase?.case_reference || `#${loanCaseId}`}
        </span>
      </div>

      {/* ===================================================
        WORKFLOW BANNERS
        =================================================== */}

      {isExistingApproval ? (
        <div className="decision-workflow-banner decision-workflow-banner--update">
          <div className="decision-workflow-banner__icon">
            <Info size={18} />
          </div>

          <div>
            <strong>
              {t("workflow.updateTitle", "Updating an approved decision")}
            </strong>

            <p>
              {t(
                "workflow.updateText",
                "The lending decision remains approved. You can update the financing terms when the detailed renovation plan provides revised renovation costs. This updates the existing loan and does not create an additional loan.",
              )}
            </p>
          </div>
        </div>
      ) : null}

      {isRejectedView ? (
        <div className="decision-workflow-banner decision-workflow-banner--locked">
          <div className="decision-workflow-banner__icon">
            <LockKeyhole size={18} />
          </div>

          <div>
            <strong>{t("workflow.rejectedTitle", "Final rejection")}</strong>

            <p>
              {t(
                "workflow.rejectedText",
                "This final rejected decision is read-only and cannot be modified.",
              )}
            </p>
          </div>
        </div>
      ) : null}

      {/* ===================================================
        ROW 1 — CONTEXT + CORE DECISION
        Equal-height cards
        =================================================== */}

      <div className="lender-submit-decision__top-grid">
        {/* =================================================
          LEFT — CONTEXT
          ================================================= */}

        <section className="decision-context-card">
          <div className="decision-card__header">
            <div>
              <h2 className="decision-card__title">{t("context.title")}</h2>

              <p className="decision-card__subtitle">{t("context.subtitle")}</p>
            </div>

            <div
              className={`decision-source-badge ${
                loanCase?.source === "REFERENCE_IMPORT"
                  ? "decision-source-badge--reference"
                  : "decision-source-badge--manual"
              }`}
            >
              {sourceLabel}
            </div>
          </div>

          <div className="decision-context-card__body">
            {/* HOMEOWNER */}

            <section className="decision-context-section">
              <div className="decision-context-grid">
                <ApplicantContextField applicant={applicant} />

                <ContextField
                  label={t("context.phone")}
                  value={applicant?.phone}
                />

                <ContextField
                  label={t("context.countryOfResidence")}
                  value={applicant?.country_of_residence_label}
                />

                <ContextField
                  label={t("context.nationality")}
                  value={applicant?.nationality_label}
                />
              </div>
            </section>

            {/* PROPERTY */}

            <section className="decision-context-section">
              <div className="decision-context-section__heading">
                <Home size={17} />

                <h3>{t("context.propertyInformation")}</h3>
              </div>

              <div className="decision-context-grid">
                <ContextField
                  label={t("context.address")}
                  value={propertyAddress}
                  icon={<MapPin size={15} />}
                  wide
                />

                <ContextField
                  label={t("context.region")}
                  value={marketRegionName}
                />

                <ContextField
                  label={t("context.propertyType")}
                  value={formatEnumLabel(property?.property_type)}
                />

                <ContextField
                  label={t("context.currentEpcRating")}
                  value={property?.epc_label || property?.epc_value}
                />

                <ContextField
                  label={t("context.usableArea")}
                  value={
                    property?.usable_area_sqm
                      ? `${property.usable_area_sqm} m²`
                      : "—"
                  }
                />
              </div>
            </section>

            {/* FINANCING CONTEXT */}

            {loanCaseData?.financing_details || loanCaseData?.result ? (
              <section className="decision-context-section">
                <div className="decision-context-section__heading">
                  <Wallet size={17} />

                  <h3>
                    {loanCase?.source === "MANUAL_ENTRY"
                      ? t(
                          "context.recordedFinancingContext",
                          "Recorded financing context",
                        )
                      : t(
                          "context.financingRequest",
                          "Original financing request",
                        )}
                  </h3>
                </div>

                <div className="decision-context-grid">
                  <ContextField
                    label={t(
                      "context.propertyPurchasePrice",
                      "Property purchase price",
                    )}
                    value={formatCurrencyDisplay(
                      loanCase?.source === "MANUAL_ENTRY"
                        ? (loanCaseData?.result?.property_price ??
                            loanCaseData?.financing_details
                              ?.property_purchase_price)
                        : loanCaseData?.financing_details
                            ?.property_purchase_price,
                    )}
                  />

                  <ContextField
                    label={t(
                      "context.initialRenovationBudget",
                      "Initial renovation estimate",
                    )}
                    value={formatCurrencyDisplay(
                      loanCaseData?.financing_details
                        ?.estimated_renovation_budget,
                    )}
                  />

                  <ContextField
                    label={
                      loanCase?.source === "MANUAL_ENTRY"
                        ? t(
                            "context.recordedLenderFinancing",
                            "Recorded lender financing",
                          )
                        : t(
                            "context.requestedFinancing",
                            "Requested total financing",
                          )
                    }
                    value={formatCurrencyDisplay(
                      loanCase?.source === "MANUAL_ENTRY"
                        ? loanCaseData?.result?.approved_total_loan_amount
                        : loanCaseData?.financing_details
                            ?.requested_total_financing_amount,
                    )}
                  />

                  <ContextField
                    label={
                      loanCase?.source === "MANUAL_ENTRY"
                        ? t("context.loanTenure", "Loan tenure")
                        : t("context.requestedTenure", "Requested tenure")
                    }
                    value={
                      (
                        loanCase?.source === "MANUAL_ENTRY"
                          ? loanCaseData?.result?.tenure_years
                          : loanCaseData?.financing_details?.tenure_years
                      )
                        ? `${
                            loanCase?.source === "MANUAL_ENTRY"
                              ? loanCaseData.result.tenure_years
                              : loanCaseData.financing_details.tenure_years
                          } ${t("common.years", "years")}`
                        : "—"
                    }
                  />
                </div>

                <div className="decision-reference-note">
                  <Info size={15} />

                  <span>
                    {loanCase?.source === "MANUAL_ENTRY"
                      ? t(
                          "context.manualFinancingNotice",
                          "These values were recorded when the lender brought the already agreed financing into LEG-UP. The renovation estimate may later be revised after the detailed renovation plan is available.",
                        )
                      : t(
                          "context.initialEstimateNotice",
                          "The renovation amount above is the original dossier estimate. The detailed renovation plan may later provide revised renovation costs.",
                        )}
                  </span>
                </div>
              </section>
            ) : null}
          </div>

          <div className="decision-context-card__footer-note">
            <ShieldCheck size={17} />

            <p>{t("context.informationalNotice")}</p>
          </div>
        </section>

        {/* =================================================
          RIGHT — CORE LENDING DECISION
          One field per row
          ================================================= */}

        <section className="decision-final-card">
          <div className="decision-card__header">
            <div>
              <h2 className="decision-card__title">
                {isExistingApproval
                  ? t("decision.updateTitle", "Review approved financing")
                  : isRejectedView
                    ? t("decision.viewTitle", "Recorded decision")
                    : t("decision.title")}
              </h2>

              <p className="decision-card__subtitle">
                {isExistingApproval
                  ? t(
                      "decision.updateSubtitle",
                      "Review and update the approved financing terms without creating a new loan case.",
                    )
                  : t("decision.subtitle")}
              </p>
            </div>

            <div className="decision-pill decision-pill--neutral decision-pill--with-icon">
              <ShieldCheck size={15} />

              {t("decision.auditLogged")}
            </div>
          </div>

          <div className="decision-final-card__body">
            {/* DECISION SWITCH */}

            <div
              className={`decision-switch ${
                decision === "APPROVED"
                  ? "decision-switch--grant"
                  : "decision-switch--reject"
              } ${isDecisionLocked ? "decision-switch--locked" : ""}`}
            >
              <button
                type="button"
                disabled={isDecisionLocked}
                className={`decision-switch__btn ${
                  decision === "APPROVED" ? "decision-switch__btn--active" : ""
                }`}
                onClick={() => {
                  if (!isDecisionLocked && decision !== "APPROVED") {
                    setDecision("APPROVED");
                    setHasUnsavedChanges(true);
                  }
                }}
              >
                {t("decision.grantLoan")}
              </button>

              <button
                type="button"
                disabled={isDecisionLocked}
                className={`decision-switch__btn ${
                  decision === "REJECTED" ? "decision-switch__btn--active" : ""
                }`}
                onClick={() => {
                  if (!isDecisionLocked && decision !== "REJECTED") {
                    setDecision("REJECTED");
                    setHasUnsavedChanges(true);
                  }
                }}
              >
                {t("decision.rejectApplication")}
              </button>
            </div>

            <div key={decision} className="decision-form-panel">
              {decision === "APPROVED" ? (
                <div className="decision-core-fields">
                  {/* 1 */}

                  <NumericField
                    icon={<Euro size={15} />}
                    label={t("fields.approvedLoanAmount", "Total lender loan")}
                    tooltip={t(
                      "tooltips.approvedLoanAmount",
                      "Automatically calculated as purchase financing plus lender renovation financing.",
                    )}
                    value={grantForm.approved_loan_amount}
                    onChange={() => {}}
                    placeholder="150,000"
                    readOnly
                    error={approvalValidation.fieldErrors.approved_loan_amount}
                    warning={
                      approvalValidation.fieldWarnings.approved_loan_amount
                    }
                  />

                  {/* 2 */}

                  <NumericField
                    icon={<Home size={15} />}
                    label={t("fields.purchaseLoanAmount")}
                    tooltip={t("tooltips.purchaseLoanAmount")}
                    value={grantForm.purchase_loan_amount}
                    onChange={updateGrant("purchase_loan_amount")}
                    placeholder="120,000"
                    readOnly={isRejectedView}
                    error={approvalValidation.fieldErrors.purchase_loan_amount}
                  />

                  {/* 3 */}

                  <NumericField
                    icon={<PiggyBank size={15} />}
                    label={t("fields.ownContribution")}
                    tooltip={t("tooltips.ownContribution")}
                    value={grantForm.own_contribution}
                    onChange={updateGrant("own_contribution")}
                    placeholder="20,000"
                    readOnly={isRejectedView}
                    error={approvalValidation.fieldErrors.own_contribution}
                  />

                  {/* 4 */}

                  <NumericField
                    icon={<Clock3 size={15} />}
                    label={t("fields.tenureYears")}
                    tooltip={t("tooltips.tenureYears")}
                    value={grantForm.tenure_years}
                    onChange={updateGrant("tenure_years")}
                    placeholder="30"
                    readOnly={isRejectedView}
                    error={approvalValidation.fieldErrors.tenure_years}
                  />

                  {/* 5 */}

                  <NumericField
                    icon={<BadgePercent size={15} />}
                    label={t("fields.indicativeApr")}
                    tooltip={t("tooltips.indicativeApr")}
                    value={grantForm.indicative_apr}
                    onChange={updateGrant("indicative_apr")}
                    placeholder="3.6"
                    allowDecimal
                    readOnly={isRejectedView}
                    error={approvalValidation.fieldErrors.indicative_apr}
                  />

                  {/* 6 */}

                  <div className="decision-field-group">
                    <NumericField
                      icon={<Wallet size={15} />}
                      label={t("fields.estimatedMonthlyPayment")}
                      tooltip={t("tooltips.estimatedMonthlyPayment")}
                      value={grantForm.estimated_monthly_payment}
                      onChange={(value) => {
                        setMonthlyPaymentOverridden(true);

                        updateGrant("estimated_monthly_payment")(value);
                      }}
                      placeholder="727.43"
                      allowDecimal
                      readOnly={isRejectedView}
                      warning={
                        approvalValidation.fieldWarnings
                          .estimated_monthly_payment
                      }
                    />

                    {grantForm.estimated_monthly_payment ? (
                      <div
                        className={`decision-calculation-status ${
                          monthlyPaymentOverridden
                            ? "decision-calculation-status--manual"
                            : "decision-calculation-status--automatic"
                        }`}
                      >
                        {monthlyPaymentOverridden
                          ? t("calculation.manuallyAdjusted")
                          : t("calculation.automaticallyCalculated")}
                      </div>
                    ) : null}
                  </div>

                  {/* 7 + 8 — Belgium */}

                  {dossier?.country_code === "BE" ? (
                    <>
                      <NumericField
                        icon={<Landmark size={15} />}
                        label={t("fields.mortgageAmount")}
                        tooltip={t("tooltips.mortgageAmount")}
                        value={grantForm.mortgage_amount}
                        onChange={updateGrant("mortgage_amount")}
                        placeholder="120,000"
                        readOnly={isRejectedView}
                        error={approvalValidation.fieldErrors.mortgage_amount}
                      />

                      <NumericField
                        icon={<Landmark size={15} />}
                        label={t("fields.mortgageMandateAmount")}
                        tooltip={t("tooltips.mortgageMandateAmount")}
                        value={grantForm.mortgage_mandate_amount}
                        onChange={updateGrant("mortgage_mandate_amount")}
                        placeholder="30,000"
                        readOnly={isRejectedView}
                        error={
                          approvalValidation.fieldErrors.mortgage_mandate_amount
                        }
                      />
                    </>
                  ) : null}
                </div>
              ) : (
                /* =========================================
                 REJECTION — ALSO ONE FIELD PER ROW
                 ========================================= */

                <div className="decision-core-fields">
                  <SelectField
                    icon={<FileCheck2 size={15} />}
                    label={t("fields.primaryRejectionReason")}
                    tooltip={t("tooltips.primaryRejectionReason")}
                    value={rejectForm.primary_rejection_reason}
                    onChange={updateReject("primary_rejection_reason")}
                    disabled={isRejectedView}
                    placeholder={t("common.select")}
                    options={[
                      {
                        value: "APPLICANT_DID_NOT_PROCEED",
                        label: t(
                          "closureReasons.applicantDidNotProceed",
                          "Applicant did not proceed",
                        ),
                      },
                      {
                        value: "FINANCING_NOT_POSSIBLE_WITH_THIS_LENDER",
                        label: t(
                          "closureReasons.financingNotPossible",
                          "Financing not possible with this lender",
                        ),
                      },
                      {
                        value: "CASE_CREATED_IN_ERROR",
                        label: t(
                          "closureReasons.caseCreatedInError",
                          "Case created in error",
                        ),
                      },
                    ]}
                  />

                  <TextareaField
                    label={t("fields.detailsExplanation")}
                    tooltip={t("tooltips.detailsExplanation")}
                    value={rejectForm.details_explanation}
                    onChange={updateReject("details_explanation")}
                    readOnly={isRejectedView}
                    placeholder={t("fields.detailsExplanationPlaceholder")}
                  />

                  <SelectField
                    icon={<FileCheck2 size={15} />}
                    label={t("fields.allowReapplication")}
                    tooltip={t("tooltips.allowReapplication")}
                    value={rejectForm.allow_reapplication}
                    onChange={updateReject("allow_reapplication")}
                    disabled={isRejectedView}
                    placeholder={t("common.select")}
                    options={[
                      {
                        value: "YES",
                        label: t("common.yes"),
                      },
                      {
                        value: "NO",
                        label: t("common.no"),
                      },
                    ]}
                  />

                  <SelectField
                    icon={<Clock3 size={15} />}
                    label={t("fields.reapplyAfter")}
                    tooltip={t("tooltips.reapplyAfter")}
                    value={rejectForm.reapply_after}
                    onChange={updateReject("reapply_after")}
                    disabled={
                      isRejectedView || rejectForm.allow_reapplication !== "YES"
                    }
                    placeholder={t("common.select")}
                    options={[
                      {
                        value: "1_MONTH",
                        label: t("reapplyAfterOptions.oneMonth"),
                      },
                      {
                        value: "3_MONTHS",
                        label: t("reapplyAfterOptions.threeMonths"),
                      },
                      {
                        value: "6_MONTHS",
                        label: t("reapplyAfterOptions.sixMonths"),
                      },
                      {
                        value: "1_YEAR",
                        label: t("reapplyAfterOptions.oneYear"),
                      },
                    ]}
                  />
                </div>
              )}
            </div>
          </div>
        </section>
      </div>

      {/* ===================================================
    ROW 2 — RENOVATION CONTEXT
    =================================================== */}

      <section className="decision-context-card decision-renovation-context-card">
        <div className="decision-card__header">
          <div>
            <h2 className="decision-card__title">
              {t("renovationContext.title", "Renovation context")}
            </h2>

            <p className="decision-card__subtitle">
              {isExternalRenovation
                ? t(
                    "renovationContext.externalSubtitle",
                    "Review the renovation information provided by the Homebuyer for an external One-Stop Shop.",
                  )
                : t(
                    "renovationContext.internalSubtitle",
                    "Review the renovation information and plan available for this loan case.",
                  )}
            </p>
          </div>

          <div
            className={`decision-source-badge ${
              isExternalRenovation
                ? "decision-source-badge--external"
                : hasInternalRenovationPlan
                  ? "decision-source-badge--reference"
                  : "decision-source-badge--neutral"
            }`}
          >
            {isExternalRenovation
              ? t("renovationContext.externalSource", "External OSS")
              : hasInternalRenovationPlan
                ? t("renovationContext.legupSource", "LEG-UP renovation")
                : t("renovationContext.pendingSource", "Renovation pending")}
          </div>
        </div>

        <div className="decision-context-card__body">
          {/* ===============================================
        EXTERNAL OSS INFORMATION
        =============================================== */}

          {isExternalRenovation ? (
            <>
              <section className="decision-context-section">
                <div className="decision-context-section__heading">
                  <Building2 size={17} />

                  <h3>
                    {t(
                      "renovationContext.externalOssTitle",
                      "External One-Stop Shop",
                    )}
                  </h3>
                </div>

                <div className="decision-context-grid">
                  <ContextField
                    label={t(
                      "renovationContext.ossName",
                      "Organisation / advisor",
                    )}
                    value={externalRenovation?.external_oss?.name}
                    wide
                  />

                  <ContextField
                    label={t("renovationContext.country", "Country")}
                    value={externalRenovation?.external_oss?.country_code}
                  />

                  <ContextField
                    label={t(
                      "renovationContext.contactPerson",
                      "Contact person",
                    )}
                    value={externalRenovation?.external_oss?.contact_name}
                  />

                  <ContextField
                    label={t("renovationContext.email", "Email")}
                    value={externalRenovation?.external_oss?.email}
                  />

                  <ContextField
                    label={t("renovationContext.phone", "Telephone")}
                    value={externalRenovation?.external_oss?.phone}
                  />

                  <ContextField
                    label={t("renovationContext.website", "Website")}
                    value={externalRenovation?.external_oss?.website}
                    wide
                  />
                </div>
              </section>

              {/* =============================================
            EXTERNAL RENOVATION DETAILS
            ============================================= */}

              <section className="decision-context-section">
                <div className="decision-context-section__heading">
                  <Home size={17} />

                  <h3>
                    {t(
                      "renovationContext.renovationTitle",
                      "Renovation information",
                    )}
                  </h3>
                </div>

                <div className="decision-context-grid">
                  <ContextField
                    label={t("renovationContext.summary", "Renovation summary")}
                    value={externalRenovation?.renovation?.summary}
                    wide
                  />

                  <ContextField
                    label={t(
                      "renovationContext.plannedCost",
                      "Planned renovation cost",
                    )}
                    value={formatCurrencyDisplay(
                      externalRenovation?.renovation?.planned_cost,
                    )}
                  />

                  <ContextField
                    label={t(
                      "renovationContext.expectedStart",
                      "Expected start",
                    )}
                    value={
                      externalRenovation?.renovation?.expected_start_date
                        ? formatDocumentDate(
                            externalRenovation.renovation.expected_start_date,
                          )
                        : "—"
                    }
                    icon={<CalendarDays size={15} />}
                  />

                  <ContextField
                    label={t(
                      "renovationContext.expectedCompletion",
                      "Expected completion",
                    )}
                    value={
                      externalRenovation?.renovation?.expected_completion_date
                        ? formatDocumentDate(
                            externalRenovation.renovation
                              .expected_completion_date,
                          )
                        : "—"
                    }
                    icon={<CalendarDays size={15} />}
                  />

                  {externalRenovation?.renovation?.notes ? (
                    <ContextField
                      label={t("renovationContext.notes", "Additional notes")}
                      value={externalRenovation.renovation.notes}
                      wide
                    />
                  ) : null}
                </div>
              </section>
            </>
          ) : null}

          {/* ===============================================
        RENOVATION PLAN
        =============================================== */}

          <section className="decision-context-section">
            <div className="decision-context-section__heading">
              <FileText size={17} />

              <h3>{t("renovationPlan.title", "Renovation plan")}</h3>
            </div>

            {hasRenovationPlan ? (
              <div className="decision-plan-card decision-plan-card--available">
                <div className="decision-plan-card__icon">
                  <FileCheck2 size={19} />
                </div>

                <div className="decision-plan-card__content">
                  <strong>
                    {isExternalRenovationPlan
                      ? t(
                          "renovationPlan.externalAvailable",
                          "External OSS renovation plan available",
                        )
                      : t(
                          "renovationPlan.available",
                          "Renovation plan available",
                        )}
                  </strong>

                  <span>
                    {renovationPlan?.filename ||
                      t("renovationPlan.document", "Renovation plan document")}
                  </span>

                  {renovationPlan?.uploaded_at ? (
                    <small>
                      {t("renovationPlan.uploaded", "Uploaded")}:{" "}
                      {formatDocumentDate(renovationPlan.uploaded_at)}
                    </small>
                  ) : null}
                </div>

                <button
                  type="button"
                  className="decision-plan-card__action"
                  onClick={() => setSelectedRenovationPlan(renovationPlan)}
                >
                  <FileText size={14} />

                  {t("renovationPlan.view", "View plan")}
                </button>
              </div>
            ) : (
              <div className="decision-plan-card decision-plan-card--pending">
                <div className="decision-plan-card__icon">
                  <FileClock size={19} />
                </div>

                <div className="decision-plan-card__content">
                  <strong>
                    {isExternalRenovation
                      ? t(
                          "renovationPlan.externalPending",
                          "External OSS renovation plan not available yet",
                        )
                      : t(
                          "renovationPlan.notAvailable",
                          "Renovation plan not available yet",
                        )}
                  </strong>

                  <span>
                    {isExternalRenovation
                      ? t(
                          "renovationPlan.externalPendingHelp",
                          "The Homebuyer has provided external renovation information, but the renovation plan is not currently available.",
                        )
                      : t(
                          "renovationPlan.notAvailableHelp",
                          "The lender decision may use an estimated renovation amount until the detailed plan becomes available.",
                        )}
                  </span>
                </div>
              </div>
            )}
          </section>

          {/* ===============================================
        SOURCE NOTICE
        =============================================== */}

          {isExternalRenovation ? (
            <div className="decision-reference-note">
              <Info size={15} />

              <span>
                {t(
                  "renovationContext.externalNotice",
                  "This information was provided by the Homebuyer for an external One-Stop Shop that is not registered in LEG-UP. LEG-UP does not verify the external organisation or the information provided.",
                )}
              </span>
            </div>
          ) : null}
        </div>
      </section>

      {/* ===================================================
        ROW 3 — RENOVATION FINANCING
        =================================================== */}

      {decision === "APPROVED" ? (
        <section className="decision-renovation-card">
          <div className="decision-card__header">
            <div>
              <h2 className="decision-card__title">
                {t(
                  "renovationFinancing.title",
                  "Renovation financing and funding sources",
                )}
              </h2>

              <p className="decision-card__subtitle">
                {t(
                  "renovationFinancing.subtitle",
                  "Review the renovation plan, lender renovation financing and any additional funding sources.",
                )}
              </p>
            </div>

            <span
              className={`decision-renovation-status-badge ${
                renovationFinancingStatus === "CONFIRMED"
                  ? "decision-renovation-status-badge--confirmed"
                  : "decision-renovation-status-badge--estimated"
              }`}
            >
              {renovationFinancingStatus === "CONFIRMED"
                ? t("renovationFinancing.confirmed", "Confirmed")
                : t("renovationFinancing.estimated", "Estimated")}
            </span>
          </div>

          <div className="decision-renovation-card__body">
            {/* PLAN STATUS */}

            {!hasRenovationPlan ? (
              <div className="decision-renovation-plan-notice decision-renovation-plan-notice--pending">
                <FileClock size={19} />

                <div>
                  <strong>
                    {t(
                      "renovationPlan.notAvailable",
                      "Renovation plan not available yet",
                    )}
                  </strong>

                  <p>
                    {t(
                      "renovationPlan.keepEstimated",
                      "The renovation financing should remain Estimated until the detailed renovation plan becomes available and has been reviewed.",
                    )}
                  </p>
                </div>
              </div>
            ) : renovationFinancingStatus === "ESTIMATED" ? (
              <div className="decision-renovation-plan-notice decision-renovation-plan-notice--review">
                <FileCheck2 size={19} />

                <div>
                  <strong>
                    {t(
                      "renovationPlan.availableReview",
                      "Renovation plan available — review financing",
                    )}
                  </strong>

                  <p>
                    {t(
                      "renovationPlan.availableReviewHelp",
                      "Review the detailed plan and revise the renovation loan if necessary. Mark the financing as Confirmed when the amount reflects the plan.",
                    )}
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => setSelectedRenovationPlan(renovationPlan)}
                >
                  <FileText size={15} />

                  {t("renovationPlan.view", "View plan")}
                </button>
              </div>
            ) : (
              <div className="decision-renovation-plan-notice decision-renovation-plan-notice--confirmed">
                <CheckCircle2 size={19} />

                <div>
                  <strong>
                    {t(
                      "renovationPlan.financingConfirmed",
                      "Financing confirmed against the renovation plan",
                    )}
                  </strong>

                  <p>
                    {t(
                      "renovationPlan.confirmedHelp",
                      "The current renovation financing is marked as confirmed based on the detailed renovation plan.",
                    )}
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => setSelectedRenovationPlan(renovationPlan)}
                >
                  <FileText size={15} />

                  {t("renovationPlan.view", "View plan")}
                </button>
              </div>
            )}

            {/* RENOVATION VALUES */}

            <div className="decision-renovation-main-grid">
              <NumericField
                icon={<Building2 size={15} />}
                label={t(
                  "fields.renovationLoanAmount",
                  "Lender renovation loan",
                )}
                tooltip={t("tooltips.renovationLoanAmount")}
                value={grantForm.renovation_loan_amount}
                onChange={updateGrant("renovation_loan_amount")}
                placeholder="30,000"
                readOnly={isRejectedView}
                error={approvalValidation.fieldErrors.renovation_loan_amount}
                warning={
                  approvalValidation.fieldWarnings.renovation_loan_amount
                }
              />

              <SelectField
                icon={<FileCheck2 size={15} />}
                label={t(
                  "fields.renovationFinancingStatus",
                  "Financing status",
                )}
                tooltip={t("tooltips.renovationFinancingStatus")}
                value={renovationFinancingStatus}
                onChange={updateRenovationStatus}
                disabled={isRejectedView || renovationWasConfirmed}
                options={[
                  {
                    value: "ESTIMATED",
                    label: t("renovationFinancing.estimated", "Estimated"),
                    disabled: renovationWasConfirmed,
                  },
                  {
                    value: "CONFIRMED",
                    label: t("renovationFinancing.confirmed", "Confirmed"),
                    disabled: !hasRenovationPlan,
                  },
                ]}
                error={
                  approvalValidation.fieldErrors.renovation_financing_status
                }
                warning={
                  approvalValidation.fieldWarnings.renovation_financing_status
                }
              />

              <TextareaField
                label={t(
                  "fields.renovationFinancingNotes",
                  "Renovation financing notes",
                )}
                tooltip={t("tooltips.renovationFinancingNotes")}
                value={renovationFinancingNotes}
                onChange={updateRenovationNotes}
                readOnly={isRejectedView}
                placeholder={t("fields.renovationFinancingNotesPlaceholder")}
              />
            </div>

            {/* OTHER FUNDING */}

            <div className="decision-funding-sources">
              <div className="decision-funding-sources__header">
                <div>
                  <strong>
                    {t("funding.title", "Other renovation funding")}
                  </strong>

                  <p>
                    {t(
                      "funding.subtitleGeneric",
                      "Add subsidies, soft loans or other funding that contributes to the renovation but is separate from this lender loan.",
                    )}
                  </p>
                </div>

                {!isRejectedView ? (
                  <button
                    type="button"
                    className="decision-funding-sources__add"
                    onClick={addFundingSource}
                  >
                    <Plus size={15} />

                    {t("funding.addSource", "Add funding source")}
                  </button>
                ) : null}
              </div>

              {fundingSources.length === 0 ? (
                <div className="decision-funding-sources__empty">
                  <Wallet size={19} />

                  <span>
                    {t(
                      "funding.empty",
                      "No additional renovation funding sources have been recorded.",
                    )}
                  </span>
                </div>
              ) : (
                <div className="decision-funding-sources__list">
                  {fundingSources.map((source, index) => {
                    const rowErrors =
                      approvalValidation.fundingSourceErrors[index] || {};

                    return (
                      <div
                        key={source.client_key}
                        className="decision-funding-source"
                      >
                        <div className="decision-funding-source__top">
                          <strong>
                            {t("funding.sourceNumber", {
                              number: index + 1,
                              defaultValue: "Funding source {{number}}",
                            })}
                          </strong>

                          {!isRejectedView ? (
                            <button
                              type="button"
                              className="decision-funding-source__remove"
                              onClick={() => removeFundingSource(index)}
                            >
                              <Trash2 size={14} />

                              {t("funding.remove", "Remove")}
                            </button>
                          ) : null}
                        </div>

                        <div className="decision-funding-source__grid">
                          <SelectField
                            icon={<Wallet size={15} />}
                            label={t("funding.type", "Funding type")}
                            tooltip={t("funding.typeTooltip")}
                            value={source.source_type}
                            onChange={(value) =>
                              updateFundingSource(index, "source_type", value)
                            }
                            disabled={isRejectedView}
                            placeholder={t("common.select")}
                            options={[
                              {
                                value: "SUBSIDY",
                                label: t("funding.types.subsidy"),
                              },
                              {
                                value: "SOFT_LOAN",
                                label: t("funding.types.softLoan"),
                              },
                              {
                                value: "OTHER",
                                label: t("funding.types.other"),
                              },
                            ]}
                            error={rowErrors.source_type}
                          />

                          <TextField
                            icon={<Landmark size={15} />}
                            label={t("funding.name", "Source name")}
                            tooltip={t("funding.nameTooltip")}
                            value={source.name}
                            onChange={(value) =>
                              updateFundingSource(index, "name", value)
                            }
                            readOnly={isRejectedView}
                            placeholder={
                              isFlandersCase
                                ? "Mijn VerbouwPremie / Mijn Verbouwlening"
                                : t("funding.namePlaceholder")
                            }
                            error={rowErrors.name}
                          />

                          <NumericField
                            icon={<Euro size={15} />}
                            label={t("funding.amount", "Amount (€)")}
                            tooltip={t("funding.amountTooltip")}
                            value={source.amount}
                            onChange={(value) =>
                              updateFundingSource(index, "amount", value)
                            }
                            readOnly={isRejectedView}
                            placeholder="15,000"
                            error={rowErrors.amount}
                          />

                          <SelectField
                            icon={<FileCheck2 size={15} />}
                            label={t("funding.status", "Status")}
                            tooltip={t("funding.statusTooltip")}
                            value={source.status}
                            onChange={(value) =>
                              updateFundingSource(index, "status", value)
                            }
                            disabled={isRejectedView}
                            options={[
                              {
                                value: "EXPECTED",
                                label: t("funding.expected"),
                              },
                              {
                                value: "CONFIRMED",
                                label: t("funding.confirmed"),
                              },
                            ]}
                            error={rowErrors.status}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </section>
      ) : null}

      {/* ===================================================
        ROW 4 — CONDITIONS + SUMMARY
        =================================================== */}

      {decision === "APPROVED" ? (
        <div className="decision-bottom-grid">
          <section className="decision-conditions-card">
            <div className="decision-card__header">
              <div>
                <h2 className="decision-card__title">
                  {t("fields.conditions", "Conditions")}
                </h2>

                <p className="decision-card__subtitle">
                  {t(
                    "conditions.subtitle",
                    "Record any conditions attached to the lender approval.",
                  )}
                </p>
              </div>
            </div>

            <div className="decision-conditions-card__body">
              <TextareaField
                label={t("fields.conditions")}
                tooltip={t("tooltips.conditions")}
                value={grantForm.conditions}
                onChange={updateGrant("conditions")}
                readOnly={isRejectedView}
                placeholder={t("fields.conditionsPlaceholder")}
              />
            </div>
          </section>

          <section className="decision-summary-card">
            <div className="decision-card__header">
              <div>
                <h2 className="decision-card__title">
                  {t("summary.title", "Financial summary")}
                </h2>

                <p className="decision-card__subtitle">
                  {t(
                    "summary.subtitle",
                    "Overview of the financing recorded for this loan case.",
                  )}
                </p>
              </div>
            </div>

            <div className="decision-summary-card__body">
              <div className="decision-calculation-summary">
                <div>
                  <span>{t("summary.approvedLoan", "Total lender loan")}</span>

                  <strong>
                    {formatCurrencyDisplay(decisionSummary.approvedTotal)}
                  </strong>
                </div>

                <div>
                  <span>
                    {t(
                      "summary.lenderRenovationLoan",
                      "Lender renovation loan",
                    )}
                  </span>

                  <strong>
                    {formatCurrencyDisplay(decisionSummary.approvedRenovation)}
                  </strong>
                </div>

                <div>
                  <span>
                    {t("summary.externalFunding", "Other renovation funding")}
                  </span>

                  <strong>
                    {formatCurrencyDisplay(decisionSummary.externalFunding)}
                  </strong>
                </div>

                <div>
                  <span>
                    {t(
                      "summary.totalRenovationFunding",
                      "Total renovation funding",
                    )}
                  </span>

                  <strong>
                    {formatCurrencyDisplay(
                      decisionSummary.totalRenovationFunding,
                    )}
                  </strong>
                </div>

                <div>
                  <span>
                    {t(
                      "summary.initialRenovationEstimate",
                      "Initial renovation estimate",
                    )}
                  </span>

                  <strong>
                    {formatCurrencyDisplay(
                      decisionSummary.initialRenovationEstimate,
                    )}
                  </strong>
                </div>

                <div
                  className={
                    decisionSummary.renovationFundingBalance >= 0
                      ? "decision-summary-item decision-summary-item--positive"
                      : "decision-summary-item decision-summary-item--negative"
                  }
                >
                  <span>
                    {t(
                      "summary.renovationFundingBalance",
                      "Renovation funding balance",
                    )}
                  </span>

                  <strong>
                    {decisionSummary.renovationFundingBalance > 0 ? "+" : ""}
                    {formatCurrencyDisplay(
                      decisionSummary.renovationFundingBalance,
                    )}
                  </strong>
                </div>

                <div>
                  <span>{t("summary.purchaseLtv", "Purchase LTV")}</span>

                  <strong>
                    {decisionSummary.purchaseLtv !== null
                      ? `${decisionSummary.purchaseLtv.toFixed(1)}%`
                      : "—"}
                  </strong>
                </div>

                <div>
                  <span>{t("summary.purchaseLoan", "Purchase loan")}</span>

                  <strong>
                    {formatCurrencyDisplay(decisionSummary.approvedPurchase)}
                  </strong>
                </div>

                {dossier?.country_code === "BE" ? (
                  <div>
                    <span>
                      {t("summary.securityTotal", "Total secured amount")}
                    </span>

                    <strong>
                      {formatCurrencyDisplay(decisionSummary.securityTotal)}
                    </strong>
                  </div>
                ) : null}
              </div>
            </div>
          </section>
        </div>
      ) : null}

      {/* ===================================================
        STICKY ACTION BAR
        =================================================== */}

      <div className="decision-sticky-actions">
        <div className="decision-sticky-actions__inner">
          <div className="decision-sticky-actions__meta">
            <strong>
              {isRejectedView
                ? t("decision.viewTitle", "Recorded decision")
                : isExistingApproval
                  ? t("buttons.updateFinancing", "Update financing")
                  : t("decision.title", "Lending decision")}
            </strong>

            {!isRejectedView ? (
              <span>
                {hasUnsavedChanges
                  ? t("common.unsavedChanges", "You have unsaved changes.")
                  : t("common.changesSaved", "All changes are saved.")}
              </span>
            ) : (
              <span>{t("workflow.rejectedText")}</span>
            )}
          </div>

          <div className="decision-sticky-actions__buttons">
            {isRejectedView ? (
              <button
                type="button"
                className="decision-action-btn decision-action-btn--ghost"
                onClick={handleBackToLoanCases}
              >
                <ArrowLeft size={16} />

                {t("buttons.backToLoanCases", "Back to loan cases")}
              </button>
            ) : (
              <>
                {decision === "APPROVED" ? (
                  <button
                    type="button"
                    className="decision-inline-action"
                    onClick={recalculateMonthlyPayment}
                    disabled={
                      !grantForm.approved_loan_amount ||
                      !grantForm.indicative_apr ||
                      !grantForm.tenure_years
                    }
                  >
                    {t("buttons.recalculatePayment")}
                  </button>
                ) : null}

                {canSaveDraft ? (
                  <button
                    type="button"
                    className="decision-action-btn decision-action-btn--ghost"
                    onClick={() => handleDecisionSave(true)}
                    disabled={isSavingDecision || isLoadingCase}
                  >
                    <Save size={16} />

                    {isSavingDecision
                      ? t("buttons.saving")
                      : t("buttons.saveDraft")}
                  </button>
                ) : null}

                <button
                  type="button"
                  className="decision-action-btn decision-action-btn--primary"
                  onClick={() => {
                    if (validateFinalDecision()) {
                      setShowSubmitConfirmation(true);
                    }
                  }}
                  disabled={
                    isSavingDecision ||
                    isLoadingCase ||
                    (!hasUnsavedChanges && isExistingApproval)
                  }
                >
                  <CheckCircle2 size={16} />

                  {isSavingDecision
                    ? t("buttons.submitting")
                    : isExistingApproval
                      ? t("buttons.updateFinancing", "Update financing")
                      : decision === "APPROVED"
                        ? t("buttons.submitGrantDecision")
                        : t("buttons.submitRejectDecision")}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ===================================================
        CONFIRMATION MODAL
        =================================================== */}

      {showSubmitConfirmation ? (
        <div className="decision-confirmation-backdrop">
          <div
            className="decision-confirmation-modal"
            role="dialog"
            aria-modal="true"
          >
            <h3>
              {isExistingApproval
                ? t("confirm.updateTitle", "Update approved financing?")
                : decision === "APPROVED"
                  ? t("confirm.grantTitle")
                  : t("confirm.rejectTitle")}
            </h3>

            <p>
              {isExistingApproval
                ? t("confirm.updateMessage", {
                    amount: formatCurrencyDisplay(
                      grantForm.approved_loan_amount,
                    ),

                    defaultValue:
                      "The approved lender financing will be updated to {{amount}}. This updates the existing loan decision and does not create an additional loan.",
                  })
                : decision === "APPROVED"
                  ? t("confirm.grantMessage", {
                      amount: formatCurrencyDisplay(
                        grantForm.approved_loan_amount,
                      ),
                    })
                  : t("confirm.rejectMessage")}
            </p>

            <div className="decision-confirmation-modal__actions">
              <button
                type="button"
                onClick={() => setShowSubmitConfirmation(false)}
              >
                {t("common.cancel")}
              </button>

              <button
                type="button"
                onClick={() => {
                  setShowSubmitConfirmation(false);

                  handleDecisionSave(false);
                }}
              >
                {isExistingApproval
                  ? t("buttons.confirmUpdate", "Update financing")
                  : t("common.confirm")}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* ===================================================
        RENOVATION PLAN MODAL
        =================================================== */}

      {selectedRenovationPlan ? (
        <RenovationPlanModal
          plan={selectedRenovationPlan}
          onClose={() => setSelectedRenovationPlan(null)}
        />
      ) : null}
    </div>
  );
}
